(ns minorodata.components.warehouse-usage.summary
  (:require
   ["date-fns" :refer [format parseISO]]
   ["lucide-react" :refer [ChevronDownIcon Snowflake]]
   [applied-science.js-interop :as jsi]
   [minorodata.components.home-dashboard.shared :refer [chart-colors stat-card]]
   [minorodata.components.home-dashboard.toolbar :as toolbar]
   [minorodata.components.warehouse-usage.shared :refer [chart-card empty-state ellipsize format-number state-card timeframe]]
   [minorodata.components.spinner.core :as spinner]
   [minorodata.warehouse-usage.subs :as warehouse-usage.subs]
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [recharts :refer [Bar BarChart CartesianGrid Cell LabelList ReferenceLine Tooltip XAxis YAxis]]
   [ui.button :as button]
   [ui.dropdown-menu :as dropdown-menu]
   [ui.typography :as typography]))

(def ^:private chart-tick-class "text-xs leading-4 text-kleene-text-secondary font-[IBM_Plex_Sans_Condensed]")

(defonce granularity (reagent/atom :month))

(defn- iso-date
  [date]
  (format date "yyyy-MM-dd"))

(defn- request-summary! [{:keys [from to]} granularity]
  (re-frame/dispatch [:warehouse-usage.events/init
                      {:from        (iso-date from)
                       :to          (iso-date to)
                       :granularity (name granularity)}]))

(defn refresh!
  []
  (request-summary! @timeframe @granularity))

(defn date-selector []
  [toolbar/date-range-dropdown {:timeframe @timeframe
                                :on-change (fn [new-timeframe]
                                             (reset! timeframe new-timeframe)
                                             (request-summary! new-timeframe @granularity))}])

(defn- request-granularity! [{:keys [from to]} granularity]
  (re-frame/dispatch [:warehouse-usage.events/refresh-granularity
                      {:from        (iso-date from)
                       :to          (iso-date to)
                       :granularity (name granularity)}]))

(defn- metric-cards [metrics loading?]
  [:div {:class "grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4"}
   [stat-card {:title "Total Billed Credits"
               :value (format-number (:total-billed-credits metrics))
               :loading loading?
               :tooltip "The total credits invoiced by Snowflake. Includes warehouse compute, cloud services after discount, and additional services such as AI and backup."}]
   [stat-card {:title "Total Credits"
               :value (format-number (:total-credits metrics))
               :loading loading?
               :tooltip "Warehouse compute credits plus the cloud services adjustment. Does not include additional Snowflake services such as AI or backup."}]
   [stat-card {:title "Compute Credits"
               :value (format-number (:compute-credits metrics))
               :loading loading?
               :tooltip "Credits consumed by warehouses while running queries and processing workloads."}]
   [stat-card {:title "Cloud Services Credits"
               :value (format-number (:cloud-services-credits metrics))
               :loading loading?
               :tooltip "Credits used by Snowflake background services such as authentication, metadata, and query optimization. Charged only when they exceed the applicable compute-credit threshold."}]])

(defn- granularity-label [g]
  (case g :week "Week" :month "Month" :quarter "Quarter" :year "Year" "Day"))

(defn- granularity-control []
  [dropdown-menu/dropdown-menu-root
   [dropdown-menu/dropdown-menu-trigger
    [button/button {:variant :secondary
                    :size :small
                    :icon-right ChevronDownIcon}
     (str "Date Granularity: " (granularity-label @granularity))]]
   [dropdown-menu/dropdown-menu-content {:align "end"}
    [dropdown-menu/dropdown-menu-radio-group
     {:value (name @granularity)
      :on-value-change (fn [v]
                         (let [g (keyword v)]
                           (reset! granularity g)
                           (request-granularity! @timeframe g)))}
     [dropdown-menu/dropdown-menu-radio-item {:value "day"} "Day"]
     [dropdown-menu/dropdown-menu-radio-item {:value "week"} "Week"]
     [dropdown-menu/dropdown-menu-radio-item {:value "month"} "Month"]
     [dropdown-menu/dropdown-menu-radio-item {:value "quarter"} "Quarter"]
     [dropdown-menu/dropdown-menu-radio-item {:value "year"} "Year"]]]])

(defn- segment-label [value]
  (when (>= value 20) (format-number value)))

(def ^:private max-total-label-buckets 20)

(defn- total-label-visible? [granularity rows]
  (case granularity
    :day  false
    :week (<= (count rows) max-total-label-buckets)
    true))

(defn- axis-date-format [granularity]
  (case granularity
    :year    "yyyy"
    :quarter "QQQ yyyy"
    :month   "MMM yyyy"
    "d MMM"))

(defn- tooltip-date-format [granularity]
  (case granularity
    :year    "yyyy"
    :quarter "QQQ yyyy"
    :month   "MMMM yyyy"
    "d MMM yyyy"))

(defn- credits-used-chart [rows series loading?]
  [chart-card
   "Credits Used"
   [granularity-control]
   (cond
     loading?
     [:div {:class "flex h-[300px] w-full items-center justify-center"}
      [spinner/with-text {:text "Loading credits used chart..."}]]

     (empty? rows)
     [empty-state Snowflake "No usage data for this period yet."]

     :else
     [:div {:class "flex min-w-0 gap-6"}
      [:div {:class "min-w-0 flex-1"}
       [:> BarChart {:width "100%" :height 300 :data rows :margin #js {:top 20 :bottom 20}}
        [:> CartesianGrid {:vertical false}]
        [:> XAxis {:dataKey warehouse-usage.subs/bucket-date-key
                   :axis-line false
                   :angle -30
                   :height 55
                   :text-anchor "end"
                   :tick {:class chart-tick-class}
                   :tick-formatter #(format (parseISO %) (axis-date-format @granularity))}]
        [:> YAxis {:type "number" :axis-line false :tick-line false :tick {:class chart-tick-class}}]
        [:> Tooltip {:cursor false
                     :label-formatter #(format (parseISO %) (tooltip-date-format @granularity))}]
        (doall
         (for [[index warehouse] (map-indexed vector series)]
           [:> Bar {:key warehouse
                    :dataKey warehouse
                    :name warehouse
                    :stackId "credits"
                    :fill (nth chart-colors index "var(--color-slate-500)")}
            (when (not (#{:day :week} @granularity))
              [:> LabelList {:dataKey warehouse :position "center" :formatter segment-label :class chart-tick-class}])
            (when (and (= index (dec (count series))) (total-label-visible? @granularity rows))
              [:> LabelList {:dataKey warehouse-usage.subs/bucket-total-key :position "top" :formatter format-number :class chart-tick-class}])]))]]
      [:div {:class "w-44 shrink-0 self-center"}
       [typography/h4 {:class "mb-3"} "Warehouse Name"]
       [:div {:class "flex flex-col gap-2"}
        (for [[index warehouse] (map-indexed vector series)]
          ^{:key warehouse}
          [:div {:class "flex min-w-0 items-center gap-2"}
           [:span {:class "h-2.5 w-2.5 shrink-0 rounded-sm"
                   :style {:background-color (nth chart-colors index "var(--color-slate-500)")}}]
           [:span {:class "min-w-0 truncate text-xs leading-4 text-kleene-text-secondary font-[IBM_Plex_Sans_Condensed]"
                   :title warehouse}
            (ellipsize warehouse 22)]])]]])])

(def ^:private value-label-inset 6)
(def ^:private value-label-gap 6)
(def ^:private value-label-char-width 6)

(defn- value-label-fits?
  [text width]
  (<= (+ (* value-label-char-width (count text)) (* 2 value-label-inset)) width))

(defn- horizontal-bar-value-label
  [props]
  (let [x         (jsi/get-in props [:viewBox :x])
        y         (jsi/get-in props [:viewBox :y])
        width     (jsi/get-in props [:viewBox :width])
        height    (jsi/get-in props [:viewBox :height])
        value     (jsi/get props :value)
        formatted (format-number value)
        negative? (neg? value)
        inside?   (or negative? (value-label-fits? formatted width))]
    (reagent/as-element
     [:text {:x (cond
                  negative? (+ x value-label-inset)
                  inside?   (- (+ x width) value-label-inset)
                  :else     (+ x width value-label-gap))
             :y (+ y (/ height 2))
             :dy 4
             :text-anchor (cond
                            negative? "start"
                            inside?   "end"
                            :else     "start")
             :class (str chart-tick-class " " (if inside? "fill-white" "fill-kleene-text-secondary"))}
      formatted])))

(defn- service-category-chart [rows loading?]
  [chart-card
   "Credits by Service Category"
   nil
   (cond
     loading?
     [:div {:class "flex h-[300px] w-full items-center justify-center"}
      [spinner/with-text {:text "Loading service category chart..."}]]

     (empty? rows)
     [empty-state Snowflake "No usage data for this period yet."]

     :else
     [:> BarChart {:width "100%" :height 300 :data rows :layout "vertical"}
      [:> CartesianGrid {:horizontal false}]
      [:> XAxis {:type "number"
                 :domain #js [(fn [data-min] (js/Math.min 0 data-min))
                              (fn [data-max] (js/Math.max 0 data-max))]
                 :axis-line false
                 :tick-line false
                 :tick {:class chart-tick-class}
                 :tick-formatter format-number}]
      [:> YAxis {:dataKey "name"
                 :type "category"
                 :axis-line false
                 :tick-line false
                 :tick {:class chart-tick-class
                        :width 190}
                 :width 220}]
      [:> ReferenceLine {:x 0 :stroke "var(--color-kleene-text-secondary)"}]
      [:> Tooltip {:cursor false}]
      [:> Bar {:dataKey "credits" :name "Credits" :radius 5}
       (for [[index _] (map-indexed vector rows)]
         [:> Cell {:key index :fill (nth chart-colors index "var(--color-slate-500)")}])
       [:> LabelList {:dataKey "credits" :content horizontal-bar-value-label}]]])])

(defn- category-chart [title rows color loading?]
  [chart-card
   title
   nil
   (cond
     loading?
     [:div {:class "flex h-[300px] w-full items-center justify-center"}
      [spinner/with-text {:text (str "Loading " title "...")}]]

     (empty? rows)
     [empty-state Snowflake "No usage data for this period yet."]

     :else
     [:> BarChart {:width "100%" :height 300 :data rows :layout "vertical"}
      [:> CartesianGrid {:horizontal false}]
      [:> XAxis {:type "number" :hide true}]
      [:> YAxis {:dataKey "name"
                 :type "category"
                 :axis-line false
                 :tick-line false
                 :tick {:class chart-tick-class}
                 :tick-formatter #(ellipsize % 14)
                 :width 110}]
      [:> Tooltip {:cursor false}]
      [:> Bar {:dataKey "credits" :name "Credits" :fill color :radius 5}
       [:> LabelList {:dataKey "credits" :content horizontal-bar-value-label}]]])])

(defn- metrics-section [status metrics]
  [:div {:class "md:col-span-2 xl:col-span-4"}
   (case status
     :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
     :error            [state-card [empty-state Snowflake "Failed to load Warehouse usage data."]]
     :loaded           [metric-cards metrics false]
     [metric-cards metrics true])])

(defn- credits-used-section [status rows series]
  [:div {:class "xl:col-span-2"}
   (case status
     :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
     :error            [state-card [empty-state Snowflake "Failed to load Warehouse usage data."]]
     :loaded           [credits-used-chart rows series false]
     [credits-used-chart rows series true])])

(defn- service-category-section [status rows]
  [:div {:class "xl:col-span-2"}
   (case status
     :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
     :error            [state-card [empty-state Snowflake "Failed to load Warehouse usage data."]]
     :loaded           [service-category-chart rows false]
     [service-category-chart rows true])])

(defn- category-section [title status rows color]
  [:div {:class "xl:col-span-2"}
   (case status
     :not-provisioned [state-card [empty-state Snowflake "Snowflake usage isn't available for your account."]]
     :error            [state-card [empty-state Snowflake "Failed to load Warehouse usage data."]]
     :loaded           [category-chart title rows color false]
     [category-chart title rows color true])])

(defn- usage-dashboard [view-model sb-status uwu-status wc-status]
  [:div {:class "grid grid-cols-1 gap-6 pt-4 md:grid-cols-2 xl:grid-cols-4"}
   [metrics-section sb-status (:metrics view-model)]
   [credits-used-section wc-status (:credits-used view-model) (:credit-series view-model)]
   [service-category-section sb-status (:service-categories view-model)]
   [category-section "Total Credits by User Name" uwu-status (:users view-model) (nth chart-colors 4)]
   [category-section "Total Credits by Warehouse" uwu-status (:warehouses view-model) (nth chart-colors 1)]])

(defn usage-tab []
  (reagent/create-class
   {:display-name
    "warehouse-usage-tab"

    :constructor
    #(request-summary! @timeframe @granularity)

    :render
    (fn []
      (let [sb-status  @(re-frame/subscribe [:warehouse-usage.subs/service-breakdown-status])
            sb         @(re-frame/subscribe [:warehouse-usage.subs/service-breakdown])
            uwu-status @(re-frame/subscribe [:warehouse-usage.subs/user-warehouse-usage-status])
            uwu        @(re-frame/subscribe [:warehouse-usage.subs/user-warehouse-usage])
            wc-status  @(re-frame/subscribe [:warehouse-usage.subs/warehouse-credits-status])
            wc         @(re-frame/subscribe [:warehouse-usage.subs/warehouse-credits])
            view-model (warehouse-usage.subs/build-view-model
                        {:service-breakdown     (or sb [])
                         :user-warehouse-usage  (or uwu [])
                         :warehouse-series      (or wc [])})]
        [:div
         [usage-dashboard view-model sb-status uwu-status wc-status]]))}))
